/**
 * Admin services page — list, add, edit, and delete services for a tenant.
 *
 * Skill / resource-feature requirements are intentionally out of scope
 * for this page in MVP — they need their own admin UI (skills and
 * resource features are first-class entities). A service created here
 * has no skill or feature requirements, meaning any active staff can
 * fulfill it. The translations admin can localise the service name
 * once it's been created.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@bookaburra/ui';
import {
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  GripVertical,
  Languages,
  Lock,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  fetchAdminServices,
  type AdminServiceWithTranslations,
} from '../booking-flow/translations-api';
import {
  CLIENT_ELIGIBILITY_LABELS,
  DEFAULT_ALLOCATION_PRIORITY,
  STAFF_TYPE_DESCRIPTIONS,
  STAFF_TYPE_LABELS,
  ServicesApiError,
  archiveService,
  createService,
  duplicateService,
  restoreService,
  updateService,
  type ClientEligibility,
  type ResourceStrategy,
  type ServiceInput,
  type ServiceLocationType,
  type StaffStrategy,
  type StaffType,
} from '../booking-flow/services-api';
import { fetchCapabilities } from '../booking-flow/capabilities-api';
import { fetchBookingTenant } from '../booking-flow/api';
import {
  fetchTenantSettings,
  type BusinessHours,
  type BusinessHoursDay,
} from '../booking-flow/tenant-settings-api';
import { useDocumentTitle } from '../lib/use-document-title';
import { atOurPlaceLabel } from '../lib/address';

export const Route = createFileRoute('/admin/$slug/services')({
  component: ServicesPage,
});

/** Build the location dropdown labels for a tenant. The on_premises
 *  label embeds the tenant's short address when one's set so admins
 *  see "At 9 Help St, Chatswood" instead of the generic "At our place". */
function locationLabels(tenantAddress: string | null): Record<ServiceLocationType, string> {
  return {
    on_premises: atOurPlaceLabel(tenantAddress),
    on_site: 'At the client\u2019s place',
    virtual: 'Virtual (video / online)',
    phone: 'Phone call',
  };
}

function ServicesPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-services', slug],
    queryFn: () => fetchAdminServices(slug),
  });

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Services · ${tenantQ.data.tenant.name} Admin` : 'Services',
  );

  const [dialogTarget, setDialogTarget] = useState<
    | { kind: 'new' }
    | { kind: 'edit'; service: AdminServiceWithTranslations }
    | null
  >(null);

  // Most-recently-archived service id so we can show a one-shot "Undo"
  // banner alongside the list. Cleared when the user dismisses it,
  // restores, or archives a different service.
  const [recentlyArchivedId, setRecentlyArchivedId] = useState<string | null>(null);

  const archiveMut = useMutation({
    mutationFn: (serviceId: string) => archiveService(slug, serviceId),
    onSuccess: (_, serviceId) => {
      setRecentlyArchivedId(serviceId);
      qc.invalidateQueries({ queryKey: ['admin-services', slug] });
    },
  });

  const restoreMut = useMutation({
    mutationFn: (serviceId: string) => restoreService(slug, serviceId),
    onSuccess: () => {
      setRecentlyArchivedId(null);
      qc.invalidateQueries({ queryKey: ['admin-services', slug] });
    },
  });

  // Most-recently-duplicated service name so we can show a one-shot
  // confirmation banner. Mirrors the archive banner pattern below.
  const [recentlyDuplicatedName, setRecentlyDuplicatedName] = useState<
    string | null
  >(null);

  const duplicateMut = useMutation({
    mutationFn: (serviceId: string) => duplicateService(slug, serviceId),
    onSuccess: (res) => {
      setRecentlyDuplicatedName(res.service.name);
      qc.invalidateQueries({ queryKey: ['admin-services', slug] });
    },
  });

  const { active, archived } = useMemo(() => {
    const all = data?.services ?? [];
    return {
      active: all.filter((s) => s.archivedAt == null),
      archived: all.filter((s) => s.archivedAt != null),
    };
  }, [data]);

  const recentlyArchived = useMemo(
    () => (recentlyArchivedId ? archived.find((s) => s.id === recentlyArchivedId) : null),
    [archived, recentlyArchivedId],
  );

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 sm:px-12">
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-secondary" />
          ))}
        </div>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load services</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {archiveMut.error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Couldn&rsquo;t archive service</AlertTitle>
          <AlertDescription>
            {friendlyServiceError(
              archiveMut.error instanceof ServicesApiError
                ? archiveMut.error.code
                : (archiveMut.error as Error).message,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {restoreMut.error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Couldn&rsquo;t restore service</AlertTitle>
          <AlertDescription>
            {friendlyServiceError(
              restoreMut.error instanceof ServicesApiError
                ? restoreMut.error.code
                : (restoreMut.error as Error).message,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {duplicateMut.error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Couldn&rsquo;t duplicate service</AlertTitle>
          <AlertDescription>
            {friendlyServiceError(
              duplicateMut.error instanceof ServicesApiError
                ? duplicateMut.error.code
                : (duplicateMut.error as Error).message,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {recentlyDuplicatedName ? (
        <Alert variant="info" className="mb-4">
          <AlertTitle>Duplicated &ldquo;{recentlyDuplicatedName}&rdquo;</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              The new service is active and ready to edit. Translations
              weren&rsquo;t copied — add them on the Translations page if
              needed.
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRecentlyDuplicatedName(null)}
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {recentlyArchived ? (
        <Alert variant="info" className="mb-4">
          <AlertTitle>Archived &ldquo;{recentlyArchived.name}&rdquo;</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              Clients can&rsquo;t book it any more. Existing appointments are
              unaffected.
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                leadingIcon={<Undo2 className="h-4 w-4" />}
                onClick={() => restoreMut.mutate(recentlyArchived.id)}
                loading={
                  restoreMut.isPending && restoreMut.variables === recentlyArchived.id
                }
              >
                Undo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRecentlyArchivedId(null)}
              >
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-8">
        {data ? (
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Services</h2>
                <p className="text-xs text-muted-foreground">
                  Bookable services on your booking page. Edit the name,
                  duration, and where the service is delivered.
                </p>
              </div>
              <Button
                leadingIcon={<Plus className="h-4 w-4" />}
                onClick={() => setDialogTarget({ kind: 'new' })}
              >
                Add service
              </Button>
            </div>
            {active.length === 0 && archived.length === 0 ? (
              <p className="rounded-md border border-dashed border-brand-sage/30 bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                No services yet. Add one to start taking bookings.
              </p>
            ) : active.length === 0 ? (
              <p className="rounded-md border border-dashed border-brand-sage/30 bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                No active services. Add one or restore an archived service below.
              </p>
            ) : (
              <ul className="space-y-2">
          {active.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-sage/15 bg-card px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-semibold text-foreground">
                  <span>{s.name}</span>
                  {s.bookableByClient === false ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-brand-sage/30 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-brand-sage"
                      title="Hidden from the public booking page. Only the business can create bookings of this service."
                    >
                      <Lock className="h-3 w-3" />
                      Internal only
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {locationTypeLabel(s.locationType)}
                  <span className="mx-1.5 text-muted-foreground/50">·</span>
                  {s.durationMinutes} min
                  {s.translations.length > 0 ? (
                    <>
                      <span className="mx-1.5 text-muted-foreground/50">·</span>
                      {s.translations.length} translation
                      {s.translations.length === 1 ? '' : 's'}
                    </>
                  ) : null}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Edit service"
                  leadingIcon={<Pencil className="h-4 w-4" />}
                  onClick={() => setDialogTarget({ kind: 'edit', service: s })}
                >
                  Edit
                </Button>
                <Link
                  to="/admin/$slug/translations"
                  params={{ slug }}
                  hash={`svc-${s.id}`}
                  aria-label={`Translate ${s.name}`}
                  title="Edit translations for this service"
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium text-secondary-foreground transition-colors hover:bg-accent"
                >
                  <Languages className="h-4 w-4" />
                  Translate
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Duplicate service"
                  title="Duplicate — copy this service's config into a new row"
                  leadingIcon={<Copy className="h-4 w-4" />}
                  onClick={() => duplicateMut.mutate(s.id)}
                  loading={duplicateMut.isPending && duplicateMut.variables === s.id}
                >
                  Duplicate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Archive service"
                  title="Archive — hide from booking page (can be undone)"
                  onClick={() => archiveMut.mutate(s.id)}
                  disabled={archiveMut.isPending && archiveMut.variables === s.id}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {archived.length > 0 ? (
          <Card className="p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Archived ({archived.length})</h2>
              <p className="text-xs text-muted-foreground">
                Hidden from the booking page. Existing bookings still reference
                these. Restore to make them bookable again.
              </p>
            </div>
            <ul className="space-y-2">
              {archived.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-brand-sage/15 bg-muted/30 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-muted-foreground line-through">
                    {s.name}
                  </p>
                  <p className="text-xs text-muted-foreground/80">
                    {locationTypeLabel(s.locationType)}
                    <span className="mx-1.5 text-muted-foreground/40">·</span>
                    {s.durationMinutes} min
                    {s.archivedAt ? (
                      <>
                        <span className="mx-1.5 text-muted-foreground/40">·</span>
                        archived {formatRelative(s.archivedAt)}
                      </>
                    ) : null}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={<ArchiveRestore className="h-4 w-4" />}
                  onClick={() => restoreMut.mutate(s.id)}
                  disabled={restoreMut.isPending && restoreMut.variables === s.id}
                >
                  Restore
                </Button>
              </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>

      <Dialog
        open={dialogTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDialogTarget(null);
        }}
      >
        {dialogTarget ? (
          <ServiceDialog
            slug={slug}
            target={dialogTarget}
            tenantAddress={tenantQ.data?.tenant.address ?? null}
            onClose={() => setDialogTarget(null)}
            onSaved={() => {
              setDialogTarget(null);
              qc.invalidateQueries({ queryKey: ['admin-services', slug] });
            }}
          />
        ) : null}
      </Dialog>
    </main>
  );
}

/** Default slot for a brand-new service: one staff member, no skill
 *  required (any active staff qualifies). Matches what most admins
 *  expect when first creating a service. */
const DEFAULT_NEW_SLOTS: { skills: string[]; count: number }[] = [
  { skills: [], count: 1 },
];

/**
 * Dropdown options for the Recommended booking frequency field.
 * The form state holds a key string (e.g. 'month:6'); `keyToFrequency`
 * and `frequencyToKey` translate to/from the (unit, count) the backend
 * persists.
 */
const FREQUENCY_OPTIONS: { key: string; label: string }[] = [
  { key: '', label: 'Not set' },
  { key: 'once', label: 'Once-off (no follow-up)' },
  { key: 'week:1', label: 'Weekly' },
  { key: 'week:2', label: 'Fortnightly' },
  { key: 'month:1', label: 'Monthly' },
  { key: 'month:3', label: 'Every 3 months' },
  { key: 'month:6', label: 'Every 6 months' },
  { key: 'month:9', label: 'Every 9 months' },
  { key: 'month:12', label: 'Every 12 months' },
  { key: 'month:18', label: 'Every 18 months' },
  { key: 'month:24', label: 'Every 24 months' },
];

function frequencyToKey(
  unit: 'once' | 'week' | 'month' | null | undefined,
  count: number | null | undefined,
): string {
  if (!unit) return '';
  if (unit === 'once') return 'once';
  return `${unit}:${count ?? 1}`;
}

function keyToFrequency(key: string): {
  unit: 'once' | 'week' | 'month' | null;
  count: number | null;
} {
  if (key === '') return { unit: null, count: null };
  if (key === 'once') return { unit: 'once', count: null };
  const [unit, countRaw] = key.split(':');
  if (unit !== 'week' && unit !== 'month') return { unit: null, count: null };
  const count = Number(countRaw);
  if (!Number.isInteger(count) || count < 1) return { unit: null, count: null };
  return { unit, count };
}

function ServiceDialog({
  slug,
  target,
  tenantAddress,
  onClose,
  onSaved,
}: {
  slug: string;
  target:
    | { kind: 'new' }
    | { kind: 'edit'; service: AdminServiceWithTranslations };
  tenantAddress: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const LABELS = locationLabels(tenantAddress);
  const isEditing = target.kind === 'edit';
  const initial = target.kind === 'edit' ? target.service : null;

  const [name, setName] = useState(initial?.name ?? '');
  const [duration, setDuration] = useState<string>(
    initial?.durationMinutes ? String(initial.durationMinutes) : '30',
  );
  const [locationType, setLocationType] = useState<ServiceLocationType>(
    initial?.locationType ?? 'on_premises',
  );
  const [slots, setSlots] = useState<{ skills: string[]; count: number }[]>(
    initial?.staffSlots ?? DEFAULT_NEW_SLOTS,
  );
  const [featureIds, setFeatureIds] = useState<string[]>(
    initial?.requiredFeatureIds ?? [],
  );
  const [frequencyKey, setFrequencyKey] = useState<string>(
    frequencyToKey(initial?.recommendedFrequencyUnit, initial?.recommendedFrequencyCount),
  );
  const [bookableByClient, setBookableByClient] = useState<boolean>(
    initial?.bookableByClient ?? true,
  );
  // null = inherit tenant hours. A BusinessHours object = explicit per-service override.
  const [hoursOverride, setHoursOverride] = useState<BusinessHours | null>(
    parseHoursOverrideString(initial?.businessHoursOverride ?? null),
  );
  const [hoursExpanded, setHoursExpanded] = useState<boolean>(false);

  // ADR-0014 allocator strategy controls.
  // For NEW services the form defaults to the operationally-useful
  // pair fill_first_today + prefer_same_as_staff_today. Existing
  // services keep whatever was persisted (which is `first_available`
  // for everything pre-migration).
  const [allocationPriority, setAllocationPriority] = useState<StaffType[]>(
    initial?.allocationPriority ?? DEFAULT_ALLOCATION_PRIORITY,
  );
  const [staffStrategy, setStaffStrategy] = useState<StaffStrategy>(
    initial?.staffStrategy ?? (isEditing ? 'first_available' : 'fill_first_today'),
  );
  const [resourceStrategy, setResourceStrategy] = useState<ResourceStrategy>(
    initial?.resourceStrategy ??
      (isEditing ? 'first_available' : 'prefer_same_as_staff_today'),
  );
  const [clientEligibility, setClientEligibility] = useState<ClientEligibility>(
    initial?.clientEligibility ?? 'any',
  );

  // Reset state on target change (Radix re-mounts content on open;
  // belt-and-braces).
  useEffect(() => {
    setName(initial?.name ?? '');
    setDuration(initial?.durationMinutes ? String(initial.durationMinutes) : '30');
    setLocationType(initial?.locationType ?? 'on_premises');
    setSlots(initial?.staffSlots ?? DEFAULT_NEW_SLOTS);
    setFeatureIds(initial?.requiredFeatureIds ?? []);
    setFrequencyKey(
      frequencyToKey(initial?.recommendedFrequencyUnit, initial?.recommendedFrequencyCount),
    );
    setBookableByClient(initial?.bookableByClient ?? true);
    setHoursOverride(parseHoursOverrideString(initial?.businessHoursOverride ?? null));
    setHoursExpanded(false);
    setAllocationPriority(
      initial?.allocationPriority ?? DEFAULT_ALLOCATION_PRIORITY,
    );
    setStaffStrategy(
      initial?.staffStrategy ?? (initial ? 'first_available' : 'fill_first_today'),
    );
    setResourceStrategy(
      initial?.resourceStrategy ??
        (initial ? 'first_available' : 'prefer_same_as_staff_today'),
    );
    setClientEligibility(initial?.clientEligibility ?? 'any');
  }, [initial]);

  // Plan-tier detection for the recommended-frequency upsell note. We don't
  // gate the field itself — capturing the data on every plan keeps the
  // Follow-ups list immediately useful when a business upgrades.
  const tenantSettingsQ = useQuery({
    queryKey: ['admin-tenant', slug],
    queryFn: () => fetchTenantSettings(slug),
    staleTime: 60_000,
  });
  const tenantTier = tenantSettingsQ.data?.tenant.tier ?? 'free';
  const isLarge = tenantTier === 'large';
  // Feature 3 plan gate — bookable-by-client toggle and per-service
  // hours override are Small + Large only. Free tier sees them disabled
  // with an upgrade nudge.
  const hasPaidPlan = tenantTier === 'small' || tenantTier === 'large';

  // Tenant's skill + feature catalogues for the pickers. Cached
  // briefly so opening Edit twice doesn't re-hit the API.
  const capsQ = useQuery({
    queryKey: ['admin-capabilities', slug],
    queryFn: () => fetchCapabilities(slug),
    staleTime: 30_000,
  });
  const skills = (capsQ.data?.skills ?? []).filter((s) => s.archivedAt == null);
  const features = (capsQ.data?.features ?? []).filter((f) => f.archivedAt == null);
  const skillNameById = new Map(
    (capsQ.data?.skills ?? []).map((s) => [s.id, s.name]),
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      const durationNum = Number(duration);
      const freq = keyToFrequency(frequencyKey);
      const input: ServiceInput = {
        name: name.trim(),
        durationMinutes: durationNum,
        locationType,
        staffSlots: slots,
        requiredFeatureIds: featureIds,
        recommendedFrequencyUnit: freq.unit,
        recommendedFrequencyCount: freq.count,
        allocationPriority,
        staffStrategy,
        resourceStrategy,
        clientEligibility,
      };
      // Plan-gated fields — only send when the tenant is on a paid plan.
      // The server enforces a soft check; the UI gate is the primary one.
      if (hasPaidPlan) {
        input.bookableByClient = bookableByClient;
        input.businessHoursOverride = hoursOverride ? JSON.stringify(hoursOverride) : null;
      }
      if (target.kind === 'edit') {
        return updateService(slug, target.service.id, input);
      }
      return createService(slug, input);
    },
    onSuccess: onSaved,
  });

  function updateSlot(index: number, patch: Partial<{ skills: string[]; count: number }>) {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  }
  function addSlot() {
    setSlots((prev) => [...prev, { skills: [], count: 1 }]);
  }
  function removeSlot(index: number) {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  }
  function toggleFeature(id: string) {
    setFeatureIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    saveMut.mutate();
  }

  const durationNum = Number(duration);
  const durationValid =
    Number.isInteger(durationNum) && durationNum >= 5 && durationNum <= 480;
  const canSubmit = name.trim().length >= 2 && durationValid && !saveMut.isPending;
  // Show the upsell note when an actual cadence is set (not "not set" or
  // "once-off" — neither of those drives a Follow-up entry).
  const frequencyDrivesList =
    frequencyKey !== '' && frequencyKey !== 'once';

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isEditing ? 'Edit service' : 'Add service'}</DialogTitle>
        <DialogDescription>
          The name and duration show up on your booking page. Where it
          happens controls whether travel time is factored in (when
          your staff visit the client) and which badge clients see.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Name" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. GP standard consult"
              autoFocus
              maxLength={120}
            />
          )}
        </FormField>

        <FormField
          label="Duration (minutes)"
          required
          description="Between 5 and 480."
          error={
            duration && !durationValid
              ? 'Enter a whole number between 5 and 480.'
              : undefined
          }
        >
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              type="number"
              inputMode="numeric"
              min={5}
              max={480}
              step={5}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          )}
        </FormField>

        <FormField label="Where does it happen?" required>
          {({ id }) => (
            <select
              id={id}
              value={locationType}
              onChange={(e) => setLocationType(e.target.value as ServiceLocationType)}
              className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
            >
              {(Object.keys(LABELS) as ServiceLocationType[]).map((v) => (
                <option key={v} value={v}>
                  {LABELS[v]}
                </option>
              ))}
            </select>
          )}
        </FormField>

        {locationType === 'on_premises' && !tenantAddress ? (
          <Alert variant="warning">
            <AlertTitle>Add your business address first</AlertTitle>
            <AlertDescription>
              Clients need an address to find you. Bookaburra supports{' '}
              <strong>one on-premises address per business</strong> — if you
              run a second location, set up another Bookaburra business for
              it. Add the address on{' '}
              <Link
                to="/admin/$slug/settings"
                params={{ slug }}
                className="font-medium text-primary underline-offset-2 hover:underline"
              >
                Settings
              </Link>
              .
            </AlertDescription>
          </Alert>
        ) : null}

        <FormField
          label="Recommended booking frequency"
          description="How often clients should re-book this service. Drives your Follow-ups list."
        >
          {({ id }) => (
            <select
              id={id}
              value={frequencyKey}
              onChange={(e) => setFrequencyKey(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </FormField>
        {!isLarge && frequencyDrivesList ? (
          <div className="-mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              Follow-up lists and CSV export are a{' '}
              <Link
                to="/admin/$slug/billing"
                params={{ slug }}
                className="font-semibold underline-offset-2 hover:underline"
              >
                Large business
              </Link>{' '}
              feature. Your setting is saved either way — your Follow-ups
              list will be ready to use the moment you upgrade.
            </p>
          </div>
        ) : null}

        {/*
          Feature 3 — bookable-by-client toggle + per-service business
          hours override. Both are Small + Large only; on free we show
          the controls disabled with an inline upsell. The server takes
          the override JSON the client posts, validates the shape, and
          uses it in the slots endpoint when computing the bookable
          window for that (service, date) pair.
        */}
        <section className="space-y-3 rounded-md border border-brand-sage/15 bg-card/60 p-3">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={bookableByClient}
              disabled={!hasPaidPlan}
              onChange={(e) => setBookableByClient(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-ring disabled:opacity-50"
            />
            <span className="flex-1">
              <span className="font-medium text-foreground">
                Bookable by clients
              </span>
              <span className="ml-1 text-xs text-muted-foreground">
                (uncheck to make this an internal-only service)
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                Internal-only services are hidden from the public booking
                page. Only the business can create bookings of this service
                (admin-side booking creation is coming soon).
              </span>
            </span>
          </label>

          <div className="border-t border-brand-sage/15 pt-3">
            <label className="block text-sm">
              <span className="block font-medium text-foreground">
                Who can book this service
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Restrict to first-time or existing clients. Admins always
                bypass this when creating bookings.
              </span>
              <select
                value={clientEligibility}
                onChange={(e) =>
                  setClientEligibility(e.target.value as ClientEligibility)
                }
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="any">{CLIENT_ELIGIBILITY_LABELS.any}</option>
                <option value="new_only">
                  {CLIENT_ELIGIBILITY_LABELS.new_only}
                </option>
                <option value="returning_only">
                  {CLIENT_ELIGIBILITY_LABELS.returning_only}
                </option>
              </select>
            </label>
          </div>
          {!hasPaidPlan ? <PaidPlanNudge slug={slug} /> : null}
        </section>

        <section className="rounded-md border border-brand-sage/15 bg-card/60">
          <button
            type="button"
            onClick={() => setHoursExpanded((x) => !x)}
            disabled={!hasPaidPlan}
            className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left text-sm disabled:opacity-60"
          >
            <span className="flex-1">
              <span className="block font-medium text-foreground">
                Custom business hours for this service
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {hoursOverride
                  ? `Custom: ${summariseHours(hoursOverride)}`
                  : 'Uses business hours'}
              </span>
            </span>
            {hoursExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </button>
          {hoursExpanded && hasPaidPlan ? (
            <div className="border-t border-brand-sage/15 p-3">
              <p className="mb-3 text-xs text-muted-foreground">
                Overrides the tenant&rsquo;s weekly hours for this service
                only. Useful for after-hours offerings, weekend-only
                callouts, etc. Closed days still apply on top.
              </p>
              <ServiceHoursEditor
                value={hoursOverride ?? DEFAULT_SERVICE_HOURS}
                onChange={(next) => setHoursOverride(next)}
              />
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setHoursOverride(null)}
                  disabled={hoursOverride === null}
                >
                  Reset to tenant hours
                </Button>
              </div>
            </div>
          ) : null}
          {!hasPaidPlan ? (
            <div className="border-t border-brand-sage/15 p-3">
              <PaidPlanNudge slug={slug} />
            </div>
          ) : null}
        </section>

        {/* ADR-0014 — allocator strategies + priority list. */}
        <section className="space-y-3 rounded-md border border-brand-sage/15 bg-card/60 p-3">
          <div>
            <h3 className="text-sm font-medium text-foreground">
              How bookings are assigned
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Drag to change the order staff types are tried. Top of the list goes first.
            </p>
          </div>

          <AllocationPriorityList
            priority={allocationPriority}
            onChange={setAllocationPriority}
          />

          <FormField
            label="Staff strategy"
            description="How to rank candidates within the chosen staff type."
          >
            {({ id }) => (
              <select
                id={id}
                value={staffStrategy}
                onChange={(e) => setStaffStrategy(e.target.value as StaffStrategy)}
                className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
              >
                <option value="first_available">First available</option>
                <option value="round_robin_today">Round-robin (today)</option>
                <option value="fill_first_today">Fill-first (today)</option>
                <option
                  value="prefer_closest_to_booking"
                  title="Best for on-site services (mobile trades, gardening). For non-on-site services, behaves like First available."
                >
                  Closest to booking (on-site)
                </option>
              </select>
            )}
          </FormField>

          {featureIds.length > 0 ? (
            <FormField
              label="Resource strategy"
              description="How to pick a room or piece of equipment once staff is chosen."
            >
              {({ id }) => (
                <select
                  id={id}
                  value={resourceStrategy}
                  onChange={(e) => setResourceStrategy(e.target.value as ResourceStrategy)}
                  className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                >
                  <option value="first_available">First available</option>
                  <option value="prefer_same_as_staff_today">
                    Prefer the resource the staff already used today
                  </option>
                </select>
              )}
            </FormField>
          ) : null}
        </section>

        <section>
          <h3 className="text-sm font-medium text-foreground">
            Staff needed
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Each slot is one role on the team. Tick the skills a person
            must hold for that slot, and set how many such people are
            needed. Add more slots for jobs that need distinct
            people (e.g. nurse + anaesthetist).
          </p>
          <div className="mt-3 space-y-3">
            {slots.length === 0 ? (
              <p className="rounded-md border border-dashed border-brand-sage/15 px-3 py-3 text-xs text-muted-foreground">
                No staff needed. Clients self-serve.
              </p>
            ) : null}
            {slots.map((slot, i) => (
              <div
                key={i}
                className="rounded-md border border-brand-sage/15 bg-card/60 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Slot {i + 1}
                    </p>
                    {slot.skills.length > 0 ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Each person must have the skill(s):{' '}
                        <span className="font-medium text-foreground">
                          {slot.skills
                            .map((id) => skillNameById.get(id) ?? id)
                            .join(' + ')}
                        </span>
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Any active staff member qualifies.
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove slot ${i + 1}`}
                    onClick={() => removeSlot(i)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {skills.length > 0 ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {skills.map((s) => (
                      <label
                        key={s.id}
                        className="flex cursor-pointer items-center gap-2 rounded-md border border-brand-sage/15 bg-background px-3 py-2 text-sm hover:bg-secondary/40"
                      >
                        <input
                          type="checkbox"
                          checked={slot.skills.includes(s.id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...slot.skills, s.id]
                              : slot.skills.filter((id) => id !== s.id);
                            updateSlot(i, { skills: next });
                          }}
                          className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                        />
                        <span className="truncate">{s.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-muted-foreground">
                    No skills defined yet — add some on the{' '}
                    <Link to="/admin/$slug/members" params={{ slug }} className="text-primary underline-offset-2 hover:underline">
                      Team page
                    </Link>
                    .
                  </p>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs text-muted-foreground" htmlFor={`slot-count-${i}`}>
                    How many of these are needed?
                  </label>
                  <Input
                    id={`slot-count-${i}`}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={50}
                    value={String(slot.count)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isInteger(v) && v >= 1 && v <= 50) {
                        updateSlot(i, { count: v });
                      }
                    }}
                    className="h-8 w-20"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            leadingIcon={<Plus className="h-4 w-4" />}
            onClick={addSlot}
          >
            Add another slot
          </Button>
        </section>

        {features.length > 0 ? (
          <section>
            <h3 className="text-sm font-medium text-foreground">
              Rooms / equipment needed
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Tick any features the service requires. The allocator
              picks a resource that provides each one.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {features.map((f) => (
                <label
                  key={f.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-brand-sage/15 bg-background px-3 py-2 text-sm hover:bg-secondary/40"
                >
                  <input
                    type="checkbox"
                    checked={featureIds.includes(f.id)}
                    onChange={() => toggleFeature(f.id)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="truncate">{f.name}</span>
                </label>
              ))}
            </div>
          </section>
        ) : null}

        {saveMut.error ? (
          saveMut.error instanceof ServicesApiError &&
          saveMut.error.code === 'business_hours_required' ? (
            <Alert variant="warning">
              <AlertTitle>Set business hours first</AlertTitle>
              <AlertDescription>
                Business hours bound the booking window for every service.
                Without them, the calendar shows no times. Set them on{' '}
                <Link
                  to="/admin/$slug/settings"
                  params={{ slug }}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Settings
                </Link>
                {' '}then come back to add this service.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertTitle>Couldn&rsquo;t save</AlertTitle>
              <AlertDescription>
                {saveMut.error instanceof ServicesApiError
                  ? friendlyServiceError(saveMut.error.code)
                  : (saveMut.error as Error).message}
              </AlertDescription>
            </Alert>
          )
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMut.isPending} disabled={!canSubmit}>
            {isEditing ? 'Save changes' : 'Add service'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function friendlyServiceError(code: string): string {
  switch (code) {
    case 'name_length_2_to_120':
      return 'Name must be between 2 and 120 characters.';
    case 'duration_5_to_480_minutes':
      return 'Duration must be between 5 and 480 minutes.';
    case 'invalid_location_type':
      return 'Pick a valid location: at our place, at the client\u2019s place, virtual, or phone.';
    case 'name_duration_and_location_required':
      return 'Name, duration and location are all required.';
    case 'staff_slot_count_1_to_50':
      return 'Each slot must need between 1 and 50 people.';
    case 'invalid_staff_slots':
    case 'invalid_staff_slot':
    case 'invalid_staff_slot_skills':
      return 'Staff slots couldn\u2019t be saved. Refresh and try again.';
    case 'invalid_required_feature_ids':
      return 'One of the chosen rooms / equipment isn\u2019t valid. Refresh and try again.';
    case 'invalid_skill_ids':
      return 'One of the chosen skills no longer exists. Refresh and try again.';
    case 'invalid_feature_ids':
      return 'One of the chosen rooms / equipment no longer exists. Refresh and try again.';
    case 'role_too_low':
      return 'You need admin or owner access to manage services.';
    case 'not_found':
      return 'This service no longer exists. Refresh the page.';
    default:
      return code;
  }
}

/** Coarse relative time — good enough for an "archived 2 days ago" hint. */
function formatRelative(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(epochMs).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export { friendlyServiceError };

/**
 * Plain-English label for a service's location type — surfaced in the
 * services list so the operator can see at a glance whether a service
 * runs at the business address, mobile, or remote.
 */
function locationTypeLabel(t: 'on_premises' | 'on_site' | 'virtual' | 'phone'): string {
  switch (t) {
    case 'on_premises':
      return 'At our place';
    case 'on_site':
      return "At client's place";
    case 'virtual':
      return 'Video call';
    case 'phone':
      return 'Phone call';
  }
}

/* ------------------ Per-service business-hours override ------------------ */

const HOURS_DAY_KEYS: (keyof BusinessHours)[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];
const HOURS_DAY_LABELS: Record<keyof BusinessHours, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** Sensible starting point when the operator first expands the override
 *  panel — same 9-5 weekday default the tenant settings page uses. */
const DEFAULT_SERVICE_HOURS: BusinessHours = {
  mon: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  tue: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  wed: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  thu: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  fri: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  sat: { open: false, openMinute: 9 * 60, closeMinute: 13 * 60 },
  sun: { open: false, openMinute: 9 * 60, closeMinute: 13 * 60 },
};

/** Parse the persisted override string. Returns null when the column
 *  is null/empty or fails to parse (treat malformed values as "no
 *  override" so the tenant hours kick in). */
function parseHoursOverrideString(raw: string | null): BusinessHours | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<BusinessHours>;
    const out: BusinessHours = { ...DEFAULT_SERVICE_HOURS };
    for (const day of HOURS_DAY_KEYS) {
      const d = parsed[day];
      if (d && typeof d === 'object') {
        out[day] = {
          open: Boolean(d.open),
          openMinute:
            typeof d.openMinute === 'number'
              ? d.openMinute
              : DEFAULT_SERVICE_HOURS[day].openMinute,
          closeMinute:
            typeof d.closeMinute === 'number'
              ? d.closeMinute
              : DEFAULT_SERVICE_HOURS[day].closeMinute,
        };
      }
    }
    return out;
  } catch {
    return null;
  }
}

/** One-line summary of which days are open + their hours. Used in the
 *  collapsed override section so the operator sees the gist without
 *  expanding. */
function summariseHours(hours: BusinessHours): string {
  const parts: string[] = [];
  for (const day of HOURS_DAY_KEYS) {
    const d = hours[day];
    if (!d.open) continue;
    parts.push(
      `${HOURS_DAY_LABELS[day]} ${minutesToHHMM(d.openMinute)}-${minutesToHHMM(d.closeMinute)}`,
    );
  }
  if (parts.length === 0) return 'Closed every day';
  return parts.join(', ');
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return (h ?? 0) * 60 + (m ?? 0);
}

function ServiceHoursEditor({
  value,
  onChange,
}: {
  value: BusinessHours;
  onChange: (next: BusinessHours) => void;
}) {
  function updateDay(day: keyof BusinessHours, patch: Partial<BusinessHoursDay>) {
    onChange({ ...value, [day]: { ...value[day], ...patch } });
  }
  return (
    <ul className="divide-y divide-brand-sage/15 rounded-md border border-brand-sage/15">
      {HOURS_DAY_KEYS.map((day) => {
        const d = value[day];
        return (
          <li
            key={day}
            className="grid grid-cols-[5rem_auto_1fr_auto_1fr] items-center gap-3 px-3 py-2"
          >
            <span className="text-sm font-medium text-foreground">
              {HOURS_DAY_LABELS[day]}
            </span>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={d.open}
                onChange={(e) => updateDay(day, { open: e.target.checked })}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span>{d.open ? 'Open' : 'Closed'}</span>
            </label>
            <input
              type="time"
              value={minutesToHHMM(d.openMinute)}
              disabled={!d.open}
              onChange={(e) =>
                updateDay(day, { openMinute: hhmmToMinutes(e.target.value) })
              }
              aria-label={`${HOURS_DAY_LABELS[day]} open time`}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-40"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="time"
              value={minutesToHHMM(d.closeMinute)}
              disabled={!d.open}
              onChange={(e) =>
                updateDay(day, { closeMinute: hhmmToMinutes(e.target.value) })
              }
              aria-label={`${HOURS_DAY_LABELS[day]} close time`}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-40"
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Drag-to-reorder list for the per-service allocation priority
 * (ADR-0014). Four staff-type cards, HTML5 native drag + up/down
 * arrow fallback for keyboard / no-drag environments.
 *
 * Tim's explicit constraint: NO remove action. Tenants that want
 * "this service never goes to casuals" use the existing skills or
 * service_staff_allowlist mechanism. Removing tiers here would
 * silently make the service unbookable when a type runs dry.
 */
function AllocationPriorityList({
  priority,
  onChange,
}: {
  priority: StaffType[];
  onChange: (next: StaffType[]) => void;
}) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  function move(from: number, to: number) {
    if (to < 0 || to >= priority.length || from === to) return;
    const next = [...priority];
    const [item] = next.splice(from, 1);
    if (item !== undefined) next.splice(to, 0, item);
    onChange(next);
  }

  function onDragStart(index: number) {
    setDraggingIndex(index);
  }
  function onDragOver(e: React.DragEvent<HTMLLIElement>, overIndex: number) {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === overIndex) return;
    move(draggingIndex, overIndex);
    setDraggingIndex(overIndex);
  }
  function onDragEnd() {
    setDraggingIndex(null);
  }

  return (
    <ol className="space-y-2">
      {priority.map((tier, i) => (
        <li
          key={tier}
          draggable
          onDragStart={() => onDragStart(i)}
          onDragOver={(e) => onDragOver(e, i)}
          onDragEnd={onDragEnd}
          className={`flex items-center gap-2 rounded-md border border-brand-sage/15 bg-background px-3 py-2 ${
            draggingIndex === i ? 'opacity-60' : ''
          }`}
        >
          <GripVertical
            className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground"
            aria-hidden
          />
          <span className="w-6 text-xs font-semibold tabular-nums text-muted-foreground">
            {i + 1}.
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              {STAFF_TYPE_LABELS[tier]}
            </p>
            <p
              className="text-xs text-muted-foreground"
              title={tier === 'paid_hourly' ? STAFF_TYPE_DESCRIPTIONS[tier] : undefined}
            >
              {STAFF_TYPE_DESCRIPTIONS[tier]}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={`Move ${STAFF_TYPE_LABELS[tier]} up`}
              onClick={() => move(i, i - 1)}
              disabled={i === 0}
              className="rounded p-1 text-muted-foreground hover:bg-secondary/60 disabled:opacity-40"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`Move ${STAFF_TYPE_LABELS[tier]} down`}
              onClick={() => move(i, i + 1)}
              disabled={i === priority.length - 1}
              className="rounded p-1 text-muted-foreground hover:bg-secondary/60 disabled:opacity-40"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Brand-gold tinted upsell nudge — shown next to a disabled control on
 *  the free tier. Show + disable + nudge pattern: never hide paid
 *  features, always make the upgrade path one tap away. */
function PaidPlanNudge({ slug }: { slug: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-brand-gold/40 bg-brand-gold/10 px-3 py-2 text-xs text-foreground">
      <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold" />
      <p>
        Available on Small and Large plans.{' '}
        <Link
          to="/admin/$slug/billing"
          params={{ slug }}
          className="font-semibold underline-offset-2 hover:underline"
        >
          Upgrade →
        </Link>
      </p>
    </div>
  );
}
